import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  Settings2, Plus, Edit, Trash2, CheckCircle2, XCircle, 
  Loader2, Link2, Key, User, Lock, FileText, Upload,
  ExternalLink, RefreshCw, AlertTriangle, Play, Eye, EyeOff,
  Database
} from 'lucide-react';
import { 
  getAPIConfigs, 
  createAPIConfig, 
  updateAPIConfig, 
  deleteAPIConfig, 
  verifyAPIConfig,
  uploadAPIDoc,
  testAPICall
} from '@/lib/api';
import { toast } from 'sonner';

const AUTH_TYPES = [
  { value: 'basic', label: 'Basic Auth (Username/Password)' },
  { value: 'bearer', label: 'Bearer Token (API Key)' },
  { value: 'api_key', label: 'API Key Header' },
  { value: 'custom', label: 'Custom Headers' },
];

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

export default function APIConfiguration() {
  const [configs, setConfigs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [deletingConfig, setDeletingConfig] = useState(null);
  const [verifyingId, setVerifyingId] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [showPassword, setShowPassword] = useState({});
  const [testPayload, setTestPayload] = useState('{}');
  const [testResult, setTestResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Preview data state
  const [previewingId, setPreviewingId] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewConfig, setPreviewConfig] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    api_url: '',
    username: '',
    password: '',
    api_key: '',
    auth_type: 'basic',
    request_method: 'POST',
    documentation_url: '',
    headers: {}
  });

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      const res = await getAPIConfigs();
      setConfigs(res.data);
    } catch (error) {
      console.error('Failed to load API configs:', error);
      toast.error('Failed to load API configurations');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      api_url: '',
      username: '',
      password: '',
      api_key: '',
      auth_type: 'basic',
      request_method: 'POST',
      documentation_url: '',
      headers: {}
    });
  };

  const handleSave = async () => {
    if (!formData.name || !formData.api_url) {
      toast.error('Name and API URL are required');
      return;
    }

    try {
      if (editingConfig) {
        await updateAPIConfig(editingConfig.id, formData);
        toast.success('API configuration updated');
      } else {
        await createAPIConfig(formData);
        toast.success('API configuration created');
      }
      setShowAddDialog(false);
      setEditingConfig(null);
      resetForm();
      loadConfigs();
    } catch (error) {
      toast.error('Failed to save API configuration');
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      name: config.name || '',
      description: config.description || '',
      api_url: config.api_url || '',
      username: config.username || '',
      password: '', // Don't pre-fill masked password
      api_key: '', // Don't pre-fill masked key
      auth_type: config.auth_type || 'basic',
      request_method: config.request_method || 'POST',
      documentation_url: config.documentation_url || '',
      headers: config.headers || {}
    });
    setShowAddDialog(true);
  };

  const handleDelete = async () => {
    if (!deletingConfig) return;
    try {
      await deleteAPIConfig(deletingConfig.id);
      toast.success('API configuration deleted');
      setDeletingConfig(null);
      loadConfigs();
    } catch (error) {
      toast.error('Failed to delete API configuration');
    }
  };

  const handleVerify = async (configId) => {
    setVerifyingId(configId);
    try {
      const res = await verifyAPIConfig(configId);
      if (res.data.success) {
        toast.success('API connection verified successfully!');
      } else {
        toast.error(res.data.message || 'API verification failed');
      }
      loadConfigs();
    } catch (error) {
      toast.error('Failed to verify API');
    } finally {
      setVerifyingId(null);
    }
  };

  const handlePreviewData = async (config) => {
    setPreviewingId(config.id);
    setPreviewConfig(config);
    try {
      const res = await testAPICall(config.id, {});
      setPreviewData(res.data);
      setShowPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to fetch preview data');
    } finally {
      setPreviewingId(null);
    }
  };

  const handleDocUpload = async (configId, file) => {
    try {
      await uploadAPIDoc(configId, file);
      toast.success('Documentation uploaded');
      loadConfigs();
    } catch (error) {
      toast.error('Failed to upload documentation');
    }
  };

  const handleTestCall = async (configId) => {
    setTestingId(configId);
    try {
      let payload = {};
      try {
        payload = JSON.parse(testPayload);
      } catch {
        toast.error('Invalid JSON payload');
        setTestingId(null);
        return;
      }
      
      const res = await testAPICall(configId, payload);
      setTestResult(res.data);
      if (res.data.success) {
        toast.success('Test call successful');
      } else {
        toast.error('Test call failed');
      }
    } catch (error) {
      toast.error('Test call failed');
    } finally {
      setTestingId(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-emerald-500"><CheckCircle2 className="h-3 w-3 mr-1" />Active</Badge>;
      case 'error':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Error</Badge>;
      default:
        return <Badge variant="secondary"><AlertTriangle className="h-3 w-3 mr-1" />Not Verified</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="api-configuration">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-700 to-slate-800 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-card/20 rounded-lg">
              <Settings2 className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">API Configuration</h1>
              <p className="text-muted-foreground/70 mt-1">
                Configure and manage external API integrations
              </p>
            </div>
          </div>
          <Button 
            onClick={() => { resetForm(); setShowAddDialog(true); }}
            className="bg-card text-foreground hover:bg-muted"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add API
          </Button>
        </div>
      </div>

      {/* API List */}
      {configs.length === 0 ? (
        <Card className="card-elevated">
          <CardContent className="py-12 text-center">
            <Settings2 className="h-12 w-12 text-muted-foreground/70 mx-auto mb-4" />
            <p className="text-muted-foreground font-medium">No API configurations yet</p>
            <p className="text-sm text-muted-foreground mt-1">Add your first API integration to get started</p>
            <Button className="mt-4" onClick={() => { resetForm(); setShowAddDialog(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Add API Configuration
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {configs.map((config) => (
            <Card key={config.id} className="card-elevated">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="flex items-center gap-3">
                      {config.name}
                      {getStatusBadge(config.status)}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {config.description || 'No description'}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleVerify(config.id)}
                            disabled={verifyingId === config.id}
                          >
                            {verifyingId === config.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <><RefreshCw className="h-4 w-4 mr-1" /> Verify</>
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Test API connection</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handlePreviewData(config)}
                            disabled={config.status !== 'active' || previewingId === config.id}
                            className={config.status === 'active' ? 'text-blue-600 hover:text-blue-700' : ''}
                          >
                            {previewingId === config.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Database className="h-4 w-4" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {config.status === 'active' ? 'Preview API Data' : 'Verify API first to preview data'}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    
                    <Button variant="outline" size="sm" onClick={() => handleEdit(config)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-red-600 hover:text-red-700"
                      onClick={() => setDeletingConfig(config)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="details" className="border-none">
                    <AccordionTrigger className="py-2 text-sm text-muted-foreground hover:no-underline">
                      View Configuration Details
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                        <div className="space-y-3">
                          <div className="flex items-start gap-2">
                            <Link2 className="h-4 w-4 text-muted-foreground mt-1" />
                            <div>
                              <p className="text-xs text-muted-foreground">API URL</p>
                              <p className="text-sm font-mono break-all">{config.api_url}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-start gap-2">
                            <Key className="h-4 w-4 text-muted-foreground mt-1" />
                            <div>
                              <p className="text-xs text-muted-foreground">Authentication</p>
                              <p className="text-sm capitalize">{config.auth_type?.replace('_', ' ')}</p>
                            </div>
                          </div>
                          
                          {config.username && (
                            <div className="flex items-start gap-2">
                              <User className="h-4 w-4 text-muted-foreground mt-1" />
                              <div>
                                <p className="text-xs text-muted-foreground">Username</p>
                                <p className="text-sm">{config.username}</p>
                              </div>
                            </div>
                          )}
                          
                          {config.password && (
                            <div className="flex items-start gap-2">
                              <Lock className="h-4 w-4 text-muted-foreground mt-1" />
                              <div>
                                <p className="text-xs text-muted-foreground">Password</p>
                                <p className="text-sm font-mono">{config.password}</p>
                              </div>
                            </div>
                          )}
                          
                          {config.api_key && (
                            <div className="flex items-start gap-2">
                              <Key className="h-4 w-4 text-muted-foreground mt-1" />
                              <div>
                                <p className="text-xs text-muted-foreground">API Key</p>
                                <p className="text-sm font-mono">{config.api_key}</p>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs text-muted-foreground">Request Method</p>
                            <Badge variant="outline">{config.request_method || 'POST'}</Badge>
                          </div>
                          
                          {config.last_verified && (
                            <div>
                              <p className="text-xs text-muted-foreground">Last Verified</p>
                              <p className="text-sm">{new Date(config.last_verified).toLocaleString()}</p>
                            </div>
                          )}
                          
                          {config.last_error && (
                            <div className="p-2 bg-red-50 rounded border border-red-100">
                              <p className="text-xs text-red-600 font-medium">Last Error</p>
                              <p className="text-xs text-red-500 mt-1">{config.last_error}</p>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2 pt-2">
                            {config.documentation_url && (
                              <Button variant="outline" size="sm" asChild>
                                <a href={config.documentation_url} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="h-3 w-3 mr-1" />
                                  Docs
                                </a>
                              </Button>
                            )}
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => fileInputRef.current?.click()}
                            >
                              <Upload className="h-3 w-3 mr-1" />
                              Upload Doc
                            </Button>
                            <input
                              ref={fileInputRef}
                              type="file"
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files?.[0]) {
                                  handleDocUpload(config.id, e.target.files[0]);
                                }
                              }}
                            />
                          </div>
                          
                          {config.documentation_file && (
                            <p className="text-xs text-emerald-600 flex items-center gap-1">
                              <FileText className="h-3 w-3" />
                              Documentation uploaded
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Test API Section */}
                      <div className="mt-4 pt-4 border-t border-border">
                        <p className="text-sm font-medium mb-2">Test API Call</p>
                        <div className="flex gap-2">
                          <Textarea 
                            placeholder='{"key": "value"}'
                            value={testPayload}
                            onChange={(e) => setTestPayload(e.target.value)}
                            className="font-mono text-xs h-20"
                          />
                          <Button 
                            onClick={() => handleTestCall(config.id)}
                            disabled={testingId === config.id}
                            className="flex-shrink-0"
                          >
                            {testingId === config.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <><Play className="h-4 w-4 mr-1" /> Test</>
                            )}
                          </Button>
                        </div>
                        
                        {testResult && (
                          <div className={`mt-2 p-3 rounded text-xs font-mono overflow-auto max-h-48 ${
                            testResult.success ? 'bg-emerald-500/10 dark:bg-emerald-500/20 border border-emerald-400 dark:border-emerald-600' : 'bg-red-500/10 dark:bg-red-500/20 border border-red-400 dark:border-red-600'
                          }`}>
                            <p className="font-medium mb-1">
                              Status: {testResult.status_code} {testResult.success ? '✓' : '✗'}
                            </p>
                            <pre className="whitespace-pre-wrap">
                              {typeof testResult.response === 'object' 
                                ? JSON.stringify(testResult.response, null, 2) 
                                : testResult.response || testResult.message}
                            </pre>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => {
        if (!open) {
          setShowAddDialog(false);
          setEditingConfig(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              {editingConfig ? 'Edit API Configuration' : 'Add New API Configuration'}
            </DialogTitle>
            <DialogDescription>
              Configure the API endpoint, authentication, and other settings
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Module/API Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Notes Report API"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description of what this API does"
                  rows={2}
                />
              </div>
            </div>

            {/* API URL */}
            <div>
              <Label>API URL *</Label>
              <Input
                value={formData.api_url}
                onChange={(e) => setFormData({...formData, api_url: e.target.value})}
                placeholder="https://api.example.com/endpoint"
              />
            </div>

            {/* Request Method */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Request Method</Label>
                <Select value={formData.request_method} onValueChange={(v) => setFormData({...formData, request_method: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HTTP_METHODS.map(method => (
                      <SelectItem key={method} value={method}>{method}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Authentication Type</Label>
                <Select value={formData.auth_type} onValueChange={(v) => setFormData({...formData, auth_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AUTH_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Credentials */}
            <div className="p-4 bg-muted/50 rounded-lg space-y-4">
              <p className="text-sm font-medium text-foreground">Credentials</p>
              
              {(formData.auth_type === 'basic' || formData.auth_type === 'custom') && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-1">
                      <User className="h-3 w-3" /> Username
                    </Label>
                    <Input
                      value={formData.username}
                      onChange={(e) => setFormData({...formData, username: e.target.value})}
                      placeholder="Username or email"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1">
                      <Lock className="h-3 w-3" /> Password
                    </Label>
                    <div className="relative">
                      <Input
                        type={showPassword.form ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        placeholder={editingConfig ? 'Leave empty to keep current' : 'Password'}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                        onClick={() => setShowPassword({...showPassword, form: !showPassword.form})}
                      >
                        {showPassword.form ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <Label className="flex items-center gap-1">
                  <Key className="h-3 w-3" /> API Key
                </Label>
                <div className="relative">
                  <Input
                    type={showPassword.apiKey ? 'text' : 'password'}
                    value={formData.api_key}
                    onChange={(e) => setFormData({...formData, api_key: e.target.value})}
                    placeholder={editingConfig ? 'Leave empty to keep current' : 'API Key or Token'}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setShowPassword({...showPassword, apiKey: !showPassword.apiKey})}
                  >
                    {showPassword.apiKey ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                  </Button>
                </div>
              </div>
            </div>

            {/* Documentation */}
            <div>
              <Label>Documentation URL (Optional)</Label>
              <Input
                value={formData.documentation_url}
                onChange={(e) => setFormData({...formData, documentation_url: e.target.value})}
                placeholder="https://docs.example.com/api"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddDialog(false); setEditingConfig(null); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              {editingConfig ? 'Update' : 'Add'} API Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingConfig} onOpenChange={() => setDeletingConfig(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete API Configuration?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deletingConfig?.name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Data Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-blue-600" />
              API Data Preview - {previewConfig?.name}
            </DialogTitle>
            <DialogDescription>
              Response from {previewConfig?.api_url?.substring(0, 60)}...
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Status Badge */}
            <div className="flex items-center gap-3">
              <Badge variant={previewData?.success ? 'default' : 'destructive'}>
                Status: {previewData?.status_code}
              </Badge>
              {previewData?.success ? (
                <span className="text-sm text-emerald-600 flex items-center gap-1">
                  <CheckCircle2 className="h-4 w-4" /> Success
                </span>
              ) : (
                <span className="text-sm text-red-600 flex items-center gap-1">
                  <XCircle className="h-4 w-4" /> Failed
                </span>
              )}
            </div>
            
            {/* Response Data */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-muted px-4 py-2 border-b flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">Response Data</span>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    const text = typeof previewData?.response === 'object' 
                      ? JSON.stringify(previewData.response, null, 2) 
                      : previewData?.response;
                    navigator.clipboard.writeText(text);
                    toast.success('Copied to clipboard');
                  }}
                >
                  Copy
                </Button>
              </div>
              <ScrollArea className="h-[400px]">
                <pre className="p-4 text-xs font-mono whitespace-pre-wrap bg-muted/50">
                  {typeof previewData?.response === 'object' 
                    ? JSON.stringify(previewData.response, null, 2) 
                    : previewData?.response || previewData?.message || 'No data'}
                </pre>
              </ScrollArea>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Close
            </Button>
            <Button onClick={() => handlePreviewData(previewConfig)}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
